'use client'

import React from 'react'
import {
  Layers,
  Mic,
  Link2,
  Brain,
  ArrowRight,
  ArrowDown,
  CheckCircle,
  XCircle,
} from 'lucide-react'

/**
 * AEO Implementation Diagrams
 * Visual components for the AEO implementation blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * AEO Implementation Stack - 3-layer architecture
 */
export function AEOImplementationStack() {
  const layers = [
    {
      label: 'AI Platform Layer',
      description:
        'ChatGPT, Perplexity, Gemini, AI Overviews extract structured data and cite sources',
      icon: Brain,
      color: '#F90B8A',
      items: ['Citation extraction', 'Voice summaries', 'AI Overview snippets'],
    },
    {
      label: 'Schema Layer',
      description:
        'Speakable targeting, entity @id refs, Article + FAQPage + DefinedTerm JSON-LD',
      icon: Link2,
      color: '#8b5cf6',
      items: [
        'SpeakableSpecification',
        'knowsAbout @id refs',
        'Entity graph connections',
      ],
    },
    {
      label: 'Content Layer',
      description:
        'StatBlocks, KeyTakeaways, BlogTable, front-loaded definitions, source citations',
      icon: Layers,
      color: '#008dcc',
      items: [
        '24 StatBlocks across 21 posts',
        'KeyTakeaways with data-key-takeaways',
        'Semantic HTML tables',
      ],
    },
  ]

  return (
    <div data-blog-layout='narrow' className='my-10'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-6 text-center'>
          AEO Implementation Stack
        </h3>

        <div className='space-y-4'>
          {layers.map((layer, index) => {
            const Icon = layer.icon
            return (
              <React.Fragment key={layer.label}>
                <div className='bg-background rounded-xl border border-border/60 p-5'>
                  <div className='flex items-start gap-4'>
                    <div
                      className='flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center'
                      style={{
                        backgroundColor: `${layer.color}15`,
                      }}
                    >
                      <Icon
                        className='w-5 h-5'
                        style={{ color: layer.color }}
                      />
                    </div>
                    <div className='flex-1'>
                      <div
                        className='font-semibold text-sm mb-1'
                        style={{ color: layer.color }}
                      >
                        {layer.label}
                      </div>
                      <div className='text-foreground/80 text-sm mb-3'>
                        {layer.description}
                      </div>
                      <div className='flex flex-wrap gap-2'>
                        {layer.items.map(item => (
                          <span
                            key={item}
                            className='text-xs px-2.5 py-1 rounded-full'
                            style={{
                              backgroundColor: `${layer.color}12`,
                              color: layer.color,
                            }}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {index < layers.length - 1 && (
                  <div className='flex justify-center'>
                    <ArrowDown className='w-5 h-5 text-muted-foreground' />
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>

        <div className='mt-6 text-center'>
          <p className='text-xs text-muted-foreground'>
            AI platforms extract from the content layer, guided by the schema
            layer
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Before/After AEO Comparison
 */
export function AEOBeforeAfterComparison() {
  return (
    <div data-blog-layout='narrow' className='my-10'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-6 text-center'>
          Before vs After: How AI Sees Your Content
        </h3>

        <div className='grid md:grid-cols-2 gap-6'>
          {/* Before */}
          <div className='bg-background rounded-xl border border-border/60 p-5'>
            <div className='flex items-center gap-2 mb-4'>
              <XCircle className='w-5 h-5' style={{ color: '#F90B8A' }} />
              <span
                className='font-semibold text-sm'
                style={{ color: '#F90B8A' }}
              >
                Before AEO
              </span>
            </div>

            <div className='space-y-3'>
              <div className='bg-muted rounded-lg p-3'>
                <div className='text-xs text-muted-foreground mb-1'>
                  Paragraph text
                </div>
                <p className='text-sm text-foreground/80 leading-relaxed'>
                  &quot;The Princeton study found that adding statistics
                  improved AI visibility by 30-40%, and citing sources had a
                  similar effect on citation rates across platforms.&quot;
                </p>
              </div>

              <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                <ArrowRight className='w-3 h-3' />
                <span>AI scans paragraph, may miss the data point</span>
              </div>

              <div className='bg-muted rounded-lg p-3'>
                <div className='text-xs text-muted-foreground mb-1'>
                  JSON-LD
                </div>
                <div className='text-sm text-foreground/60 font-mono'>
                  &quot;knowsAbout&quot;: [&quot;AI Product
                  Development&quot;...]
                </div>
              </div>

              <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                <ArrowRight className='w-3 h-3' />
                <span>Plain strings, no entity connections</span>
              </div>

              <div className='bg-muted rounded-lg p-3'>
                <div className='text-xs text-muted-foreground mb-1'>
                  No speakable
                </div>
                <div className='text-sm text-foreground/60'>
                  Voice assistants guess which section to read
                </div>
              </div>
            </div>
          </div>

          {/* After */}
          <div className='bg-background rounded-xl border border-border/60 p-5'>
            <div className='flex items-center gap-2 mb-4'>
              <CheckCircle className='w-5 h-5' style={{ color: '#22c55e' }} />
              <span
                className='font-semibold text-sm'
                style={{ color: '#22c55e' }}
              >
                After AEO
              </span>
            </div>

            <div className='space-y-3'>
              <div
                className='rounded-lg p-3 border text-center'
                style={{
                  backgroundColor: '#8b5cf615',
                  borderColor: '#8b5cf630',
                }}
              >
                <div className='text-xl font-bold' style={{ color: '#8b5cf6' }}>
                  30-40%
                </div>
                <div className='text-xs text-foreground/80'>
                  visibility lift from statistics
                </div>
                <div className='text-xs text-muted-foreground mt-1'>
                  Source: Princeton GEO Study, KDD 2024
                </div>
              </div>

              <div
                className='flex items-center gap-2 text-xs'
                style={{ color: '#22c55e' }}
              >
                <ArrowRight className='w-3 h-3' />
                <span>StatBlock: AI extracts the number instantly</span>
              </div>

              <div className='bg-muted rounded-lg p-3'>
                <div className='text-xs text-muted-foreground mb-1'>
                  JSON-LD
                </div>
                <div className='text-sm text-foreground/80 font-mono'>
                  &quot;knowsAbout&quot;: [{'{'}@id: &quot;#geo&quot;{'}'}...]
                </div>
              </div>

              <div
                className='flex items-center gap-2 text-xs'
                style={{ color: '#22c55e' }}
              >
                <ArrowRight className='w-3 h-3' />
                <span>Entity @id refs create authority graph</span>
              </div>

              <div
                className='rounded-lg p-3 border'
                style={{
                  backgroundColor: '#008dcc10',
                  borderColor: '#008dcc30',
                }}
              >
                <div className='flex items-center gap-2'>
                  <Mic className='w-4 h-4' style={{ color: '#008dcc' }} />
                  <span className='text-sm text-foreground/80'>
                    Speakable targets headline + takeaways
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='mt-6 text-center'>
          <p className='text-xs text-muted-foreground'>
            Same content, different structure. The AI sees the same information
            but can extract it 10x faster.
          </p>
        </div>
      </div>
    </div>
  )
}
